import { Form, Link } from '@remix-run/react';
import { useState } from 'react';

import { useSupport } from '~/components/support/SupportContext';
import { ProfilePicture } from '~/components/ui/ProfilePicture';
import { ProfilePictureUploadModal } from '~/components/ui/ProfilePictureUploadModal';
import { PushNotificationSettingsModal } from '~/components/ui/PushNotificationSettingsModal';

interface UserDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  firstName: string;
  lastName: string;
  userRole: string;
  userAvatar?: string | null;
  userEmail: string;
}

export function UserDropdown({
  isOpen,
  onClose,
  userName,
  firstName,
  lastName,
  userRole,
  userAvatar,
  userEmail,
}: UserDropdownProps) {
  const { openSupport } = useSupport();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPushNotificationModalOpen, setIsPushNotificationModalOpen] = useState(false);

  return (
    <div className="relative">
      <div
        onClick={() => onClose()}
        className="flex items-center gap-2 focus:outline-none cursor-pointer"
      >
        <button
          onClick={e => {
            e.stopPropagation();
            setIsProfileModalOpen(true);
          }}
          className="hover:ring-2 hover:ring-purple-300 rounded-full transition-all"
        >
          <ProfilePicture
            profilePictureKey={userAvatar}
            firstName={firstName}
            lastName={lastName}
            size="sm"
          />
        </button>
        <div>
          <div className="text-md font-medium text-gray-700">{userName}</div>
          <div className="text-sm text-gray-500">{userRole}</div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" aria-hidden onClick={() => onClose()} />
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20 border border-gray-200">
            <button
              onClick={() => {
                setIsProfileModalOpen(true);
                onClose();
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            >
              <span>Profile Picture</span>
            </button>
            <button
              onClick={() => {
                setIsPushNotificationModalOpen(true);
                onClose();
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            >
              <span>Push Notifications</span>
            </button>
            <Link
              to="/onboarding"
              onClick={() => onClose()}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Account Settings
            </Link>
            <button
              onClick={() => {
                openSupport();
                onClose();
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Help & Support
            </button>
            <div className="border-t border-gray-100 my-1"></div>
            <Form action="/logout" method="post" onSubmit={() => onClose()}>
              <button
                type="submit"
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
              >
                Sign out
              </button>
            </Form>
          </div>
        </>
      )}

      <ProfilePictureUploadModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentPictureKey={userAvatar}
        firstName={firstName}
        lastName={lastName}
        email={userEmail}
      />

      <PushNotificationSettingsModal
        isOpen={isPushNotificationModalOpen}
        onClose={() => setIsPushNotificationModalOpen(false)}
      />
    </div>
  );
}
